import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, TextField, IconButton,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Checkbox, Divider, Grid2, Tooltip, Chip,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { apiUrl, handleApiError, downloadBlob } from './utils/api';
import NoMatchingRecords from './components/NoMatchingRecords';
import AppDialog from './components/AppDialog';
import { useConfirm } from './components/ConfirmProvider';
import { useNotify } from './components/NotificationProvider';
import { formatDate } from './utils/date';

const EMPTY = {
  invoiceNo: '', invoiceDate: new Date().toISOString().split('T')[0],
  brokerName: '', brokerAddress: '', brokerEmail: '', brokerLoadNo: '',
  origin: '', destination: '', description: '', loadDate: '',
  rateBasis: 'FLAT', rate: '', miles: '', equipment: '', weight: '', poNumber: '',
  paymentTerms: 'DUE UPON RECEIPT', factored: true, factorName: 'Goodman Capital Finance',
  factorRemitBlock: '', status: 'DRAFT', lines: [],
};

const num = (v) => (v === '' || v == null ? 0 : Number(v) || 0);

export default function FlatbedInvoice() {
  const [user] = useAuthState(auth);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Files chosen via the Upload button, attached to the invoice (as BOL/POD) when it's saved.
  const [pendingDocs, setPendingDocs] = useState([]);
  // Documents already attached to the invoice being edited, and the upload-in-progress flag.
  const [docs, setDocs] = useState([]);
  const [docBusy, setDocBusy] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState({}); // docId -> show its per-page rows
  const [preview, setPreview] = useState(null); // { url, title } for the in-app preview dialog
  const confirm = useConfirm();
  const notify = useNotify();

  const base = `${apiUrl}/api/flatbed/invoices`;

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(base);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  // Load the documents already attached to a saved invoice (metadata only).
  const loadDocs = async (invoiceId) => {
    if (!invoiceId) { setDocs([]); return; }
    try {
      const res = await axios.get(`${base}/${invoiceId}/documents`);
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setDocs([]);
      setError(handleApiError(err, 'Failed to load attached documents'));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const computedTotal = (form.lines || []).reduce(
    (sum, l) => sum + (l.amount != null && l.amount !== '' ? num(l.amount) : num(l.quantity) * num(l.rate)), 0);

  const setLine = (idx, k, v) => setForm((f) => {
    const lines = [...f.lines];
    lines[idx] = { ...lines[idx], [k]: v };
    if (k === 'quantity' || k === 'rate') {
      lines[idx].amount = (num(lines[idx].quantity) * num(lines[idx].rate)).toFixed(2);
    }
    return { ...f, lines };
  });

  const addLine = () => setForm((f) => ({
    ...f, lines: [...f.lines, { lineDate: f.loadDate || '', description: '', quantity: '1', rate: '', amount: '' }],
  }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const resetForm = () => { setForm(EMPTY); setEditingId(null); setInfo(''); setError(''); setPendingDocs([]); setDocs([]); };

  // ---- upload (single path) ----
  // Every uploaded file is stashed and attached to the invoice PDF (as a BOL/POD) on Save. For a
  // brand-new invoice the first document is also run through OCR to prefill the header.
  const onUpload = async (file) => {
    if (!file) return;
    setPendingDocs((prev) => [...prev, file]);
    const shouldExtract = !editingId && !form.brokerName?.trim();
    if (shouldExtract) {
      await extractInto(file);
    } else {
      setInfo(`"${file.name}" will be attached to the invoice PDF when you save.`);
      notify.success('Document added');
    }
  };

  const extractInto = async (file) => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${base}/extract`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data || {};
      setForm({
        ...EMPTY, ...d,
        // The broker load number is the invoice number.
        invoiceNo: d.brokerLoadNo || d.invoiceNo || '',
        invoiceDate: d.invoiceDate || EMPTY.invoiceDate,
        loadDate: d.loadDate || '',
        rate: d.rate ?? '', miles: d.miles ?? '',
        lines: (d.lines || []).map((l) => ({
          lineDate: l.lineDate || '', description: l.description || '',
          quantity: l.quantity ?? '1', rate: l.rate ?? '', amount: l.amount ?? '',
        })),
      });
      setEditingId(null);
      setInfo('Draft extracted. Review the fields, then Save — the uploaded document will be attached to the PDF.');
    } catch (err) {
      setError(handleApiError(err, 'Extraction failed'));
    } finally {
      setBusy(false);
    }
  };

  const removePendingDoc = (idx) => setPendingDocs((prev) => prev.filter((_, i) => i !== idx));

  // ---- preview (in-app dialog with an embedded viewer) ----
  const openPreview = async (url, title) => {
    setError('');
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const objectUrl = window.URL.createObjectURL(res.data);
      setPreview({ url: objectUrl, title });
    } catch (err) {
      setError(handleApiError(err, 'Preview failed'));
    }
  };
  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  const previewInvoice = (row) => openPreview(`${base}/${row.id}/pdf`, `Invoice ${row.invoiceNo || row.id} + documents`);

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      ...EMPTY, ...row,
      invoiceDate: row.invoiceDate || '', loadDate: row.loadDate || '',
      rate: row.rate ?? '', miles: row.miles ?? '',
      lines: (row.lines || []).map((l) => ({
        lineDate: l.lineDate || '', description: l.description || '',
        quantity: l.quantity ?? '1', rate: l.rate ?? '', amount: l.amount ?? '',
      })),
    });
    setInfo(''); setError(''); setPendingDocs([]);
    loadDocs(row.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const previewDoc = (d) => openPreview(`${base}/documents/${d.id}`, d.fileName || `Document ${d.id}`);

  // Remove a document already attached to the saved invoice (one PDF "page" / attachment).
  const deleteDoc = async (d) => {
    const ok = await confirm({
      title: 'Remove attached document',
      message: `Remove "${d.fileName || 'document ' + d.id}" from this invoice? It will no longer appear in the invoice PDF.`,
      confirmText: 'Remove',
      severity: 'error',
    });
    if (!ok) return;
    setDocBusy(true); setError('');
    try {
      await axios.delete(`${base}/documents/${d.id}`);
      await loadDocs(editingId);
      notify.success('Document removed');
    } catch (err) {
      const msg = handleApiError(err, 'Remove failed');
      setError(msg);
      notify.error(msg);
    } finally {
      setDocBusy(false);
    }
  };

  const toggleDocExpanded = (docId) => setExpandedDocs((m) => ({ ...m, [docId]: !m[docId] }));

  const previewDocPage = (d, p) => openPreview(`${base}/documents/${d.id}/pages/${p}`, `${d.fileName || 'Document ' + d.id} — page ${p}`);

  // Remove a single page from a multi-page PDF attachment (backend rewrites the stored PDF).
  const deleteDocPage = async (d, p) => {
    const ok = await confirm({
      title: 'Remove page',
      message: `Remove page ${p} of "${d.fileName || 'document ' + d.id}"? This rewrites the attached PDF and can't be undone.`,
      confirmText: 'Remove page',
      severity: 'error',
    });
    if (!ok) return;
    setDocBusy(true); setError('');
    try {
      const res = await axios.delete(`${base}/documents/${d.id}/pages/${p}`);
      await loadDocs(editingId);
      notify.success(res.data?.documentDeleted ? 'Last page removed — attachment deleted' : `Page ${p} removed`);
    } catch (err) {
      const msg = handleApiError(err, 'Remove page failed');
      setError(msg);
      notify.error(msg);
    } finally {
      setDocBusy(false);
    }
  };

  const save = async () => {
    if (!form.brokerName?.trim()) { setError('Broker name is required.'); return; }
    setBusy(true); setError(''); setInfo('');
    const payload = {
      ...form,
      email: user?.email,
      rate: form.rate === '' ? null : Number(form.rate),
      miles: form.miles === '' ? null : Number(form.miles),
      lines: (form.lines || []).map((l, i) => ({
        lineDate: l.lineDate || null, description: l.description,
        quantity: l.quantity === '' ? null : Number(l.quantity),
        rate: l.rate === '' ? null : Number(l.rate),
        amount: l.amount === '' || l.amount == null ? null : Number(l.amount),
        sortOrder: i,
      })),
    };
    try {
      const wasEditing = !!editingId;
      const res = editingId
        ? await axios.put(`${base}/${editingId}`, payload)
        : await axios.post(base, payload);
      const saved = res.data;
      // Attach any uploaded documents (BOL/POD/rate con) to the saved invoice, in upload order.
      let attached = 0;
      if (saved?.id && pendingDocs.length) {
        for (const f of pendingDocs) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('docType', 'BOL');
            await axios.post(`${base}/${saved.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            attached += 1;
          } catch (e) { /* best-effort per file */ }
        }
      }
      setPendingDocs([]);
      await loadList();
      resetForm();
      setInfo(`Saved${attached ? ` with ${attached} attached document${attached === 1 ? '' : 's'}` : ''}.`);
      notify.success(`Invoice ${wasEditing ? 'updated' : 'created'}`);
    } catch (err) {
      const msg = handleApiError(err, 'Save failed');
      setError(msg);
      notify.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (row) => {
    try {
      const res = await axios.get(`${base}/${row.id}/pdf`, { responseType: 'blob' });
      downloadBlob(res, `${row.invoiceNo || 'flatbed-invoice-' + row.id}.pdf`);
    } catch (err) {
      setError(handleApiError(err, 'PDF failed'));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: 'Delete invoice',
      message: `Delete invoice ${row.invoiceNo || row.id}? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${base}/${row.id}?email=${encodeURIComponent(user?.email || '')}`);
      await loadList();
      notify.success('Invoice deleted');
    } catch (err) {
      const msg = handleApiError(err, 'Delete failed');
      setError(msg);
      notify.error(msg);
    }
  };

  const field = (label, key, opts = {}) => (
    <TextField
      label={label} value={form[key] ?? ''} onChange={(e) => set(key, e.target.value)}
      size="small" fullWidth {...opts}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Flatbed Invoice (flat rate per load)</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{editingId ? `Editing invoice #${form.invoiceNo || editingId}` : 'New invoice'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
            Upload BOL / POD
            <input type="file" accept="image/*,application/pdf" hidden
              onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ''; }} />
          </Button>
          {busy && <CircularProgress size={22} />}
          {(editingId || form.brokerName || pendingDocs.length > 0) && <Button onClick={resetForm}>Clear</Button>}
        </Stack>

        {pendingDocs.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Attaching on save:
            </Typography>
            {pendingDocs.map((f, i) => (
              <Chip key={`${f.name}-${i}`} size="small" icon={<AttachFileIcon />} label={f.name}
                onDelete={() => removePendingDoc(i)} />
            ))}
          </Stack>
        )}

        {editingId && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Attached documents (extra PDF pages after the invoice)
                {docs.length > 0 ? ` — ${docs.length}` : ''}
              </Typography>
              {docBusy && <CircularProgress size={16} />}
            </Stack>
            {docs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">None attached.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {docs.map((d) => {
                  const multiPage = d.pdf && (d.pageCount || 1) > 1;
                  const open = !!expandedDocs[d.id];
                  return (
                    <Box key={d.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
                        {multiPage ? (
                          <Tooltip title={open ? 'Hide pages' : 'Show pages'}>
                            <IconButton size="small" onClick={() => toggleDocExpanded(d.id)}>
                              {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <AttachFileIcon fontSize="small" color="action" sx={{ mx: 0.5 }} />
                        )}
                        <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                          {d.fileName || `Document ${d.id}`}
                          {d.docType ? <Chip size="small" label={d.docType} sx={{ ml: 1 }} /> : null}
                          {multiPage ? <Chip size="small" variant="outlined" label={`${d.pageCount} pages`} sx={{ ml: 1 }} /> : null}
                        </Typography>
                        <Tooltip title="Preview this document">
                          <IconButton size="small" onClick={() => previewDoc(d)}><VisibilityIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={multiPage ? 'Remove the whole document (all pages)' : 'Remove this document (page)'}>
                          <span>
                            <IconButton size="small" color="error" disabled={docBusy} onClick={() => deleteDoc(d)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      {multiPage && open && (
                        <Stack spacing={0.5} sx={{ px: 1, pb: 1, pl: 5 }}>
                          {Array.from({ length: d.pageCount }, (_, i) => i + 1).map((p) => (
                            <Stack key={p} direction="row" spacing={1} alignItems="center"
                              sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>Page {p}</Typography>
                              <Tooltip title="Preview this page">
                                <IconButton size="small" onClick={() => previewDocPage(d, p)}><VisibilityIcon fontSize="small" /></IconButton>
                              </Tooltip>
                              <Tooltip title="Remove this page">
                                <span>
                                  <IconButton size="small" color="error" disabled={docBusy} onClick={() => deleteDocPage(d, p)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            {/* Load number IS the invoice number — keep the two fields in sync. */}
            <TextField label="Load # / Invoice #" value={form.brokerLoadNo ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, brokerLoadNo: e.target.value, invoiceNo: e.target.value }))}
              size="small" fullWidth />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>{field('Invoice Date', 'invoiceDate', { type: 'date', InputLabelProps: { shrink: true } })}</Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>{field('Broker / Customer', 'brokerName', { required: true })}</Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>{field('Broker Email', 'brokerEmail')}</Grid2>
          <Grid2 size={12}>{field('Broker Address', 'brokerAddress')}</Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>{field('Route (description)', 'description')}</Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>{field('Load Date', 'loadDate', { type: 'date', InputLabelProps: { shrink: true } })}</Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>{field('Equipment', 'equipment')}</Grid2>

          <Grid2 size={{ xs: 12, sm: 3 }}>
            {field('Rate Basis', 'rateBasis', {
              select: true, children: ['FLAT', 'PER_MILE'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>),
            })}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>{field('Rate ($)', 'rate', { type: 'number' })}</Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>{field('Miles', 'miles', { type: 'number', disabled: form.rateBasis !== 'PER_MILE' })}</Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>{field('Payment Terms', 'paymentTerms')}</Grid2>
        </Grid2>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Line items</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">HRS/LOAD</TableCell>
                <TableCell align="right">Rate ($)</TableCell>
                <TableCell align="right">Amount ($)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(form.lines || []).map((l, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField type="date" size="small" value={l.lineDate || ''}
                      onChange={(e) => setLine(idx, 'lineDate', e.target.value)} InputLabelProps={{ shrink: true }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={l.description || ''}
                      onChange={(e) => setLine(idx, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 90 }} value={l.quantity ?? ''}
                      onChange={(e) => setLine(idx, 'quantity', e.target.value)} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 110 }} value={l.rate ?? ''}
                      onChange={(e) => setLine(idx, 'rate', e.target.value)} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 110 }} value={l.amount ?? ''}
                      onChange={(e) => setLine(idx, 'amount', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeLine(idx)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(form.lines || []).length === 0 && (
                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No lines — add one or upload a rate confirmation.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h6" align="right" sx={{ mt: 1 }}>Total Due: ${computedTotal.toFixed(2)}</Typography>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={<Checkbox checked={!!form.factored} onChange={(e) => set('factored', e.target.checked)} />}
          label="Factored — show the Goodman Capital Finance remittance NOTICE on the invoice"
        />
        {form.factored && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
            The standardized Goodman Capital Finance NOTICE (by mail / by wire remit box) is rendered on the invoice PDF.
          </Typography>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={save} disabled={busy}>{editingId ? 'Update' : 'Save'}</Button>
          {editingId && (
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => previewInvoice({ id: editingId, invoiceNo: form.invoiceNo })}>
              Preview invoice + docs
            </Button>
          )}
        </Stack>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Saved flatbed invoices</Typography>
      {loading && <CircularProgress />}
      {!loading && rows.length === 0 && <NoMatchingRecords />}
      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Load #</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.invoiceNo}</TableCell>
                  <TableCell>{formatDate(r.invoiceDate)}</TableCell>
                  <TableCell>{r.brokerName}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell>{r.brokerLoadNo}</TableCell>
                  <TableCell align="right">${Number(r.totalAmount || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }} title={r.updatedBy || r.createdBy || ''}>
                    {(r.updatedBy || r.createdBy || r.updatedAt || r.createdAt) ? (
                      <Stack spacing={0}>
                        {(r.updatedBy || r.createdBy) && (
                          <Box component="span" sx={{ color: 'text.primary' }}>{r.updatedBy || r.createdBy}</Box>
                        )}
                        {(r.updatedAt || r.createdAt) && (
                          <Box component="span" sx={{ color: 'text.secondary' }}>{r.updatedAt || r.createdAt}</Box>
                        )}
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Preview invoice + documents">
                      <IconButton size="small" onClick={() => previewInvoice(r)}><VisibilityIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF (invoice + documents)">
                      <IconButton size="small" onClick={() => downloadPdf(r)}><PictureAsPdfIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Button size="small" onClick={() => editRow(r)}>Edit</Button>
                    <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---- preview dialog (combined invoice PDF or a single document) ---- */}
      <AppDialog open={!!preview} onClose={closePreview} fullWidth maxWidth="lg">
        <DialogTitle>{preview?.title || 'Preview'}</DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          {preview && (
            <iframe title="document-preview" src={preview.url} style={{ width: '100%', height: '100%', border: 0 }} />
          )}
        </DialogContent>
        <DialogActions>
          {preview && <Button onClick={() => window.open(preview.url, '_blank', 'noopener')}>Open in new tab</Button>}
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
